// Chart tracking: where a patient's physical file is (docs/plans/phase2-medical-records-
// charts.md §4). A chart is filed on a records-room location when opened, then issued to a
// ward or department and returned to a location, in turn. Where it is now is its latest
// movement; movements are never changed or deleted.

import { err, ok, type Result } from '../result.ts';

export const CHART_MOVEMENT_KINDS = ['filed', 'issued', 'returned'] as const;
export type ChartMovementKind = (typeof CHART_MOVEMENT_KINDS)[number];

export const CHART_DESTINATION_KINDS = ['ward', 'service_point'] as const;
export type ChartDestinationKind = (typeof CHART_DESTINATION_KINDS)[number];

/**
 * The facility's return period, in whole hours (TODO(clinical-verify) C2: one period for
 * every destination). A data-entry bound, not a rule about how long a chart may be out.
 */
export const CHART_RETURN_HOURS = { min: 1, max: 720 } as const;

/** A few minutes cover clock differences between the desk and the server. */
const CLOCK_SKEW_MS = 5 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

/** Whether `next` may follow the chart's latest movement (`null`: none yet). */
export function chartMovementAllowed(
  last: ChartMovementKind | null,
  next: ChartMovementKind,
): boolean {
  switch (next) {
    case 'filed':
      return last === null;
    case 'issued':
      return last === 'filed' || last === 'returned';
    case 'returned':
      return last === 'issued';
  }
}

/** When a chart issued at `issuedAt` is due back; null when no return period is set. */
export function chartDueAt(issuedAt: Date, returnHours: number | null): Date | null {
  return returnHours === null ? null : new Date(issuedAt.getTime() + returnHours * HOUR_MS);
}

/** Out longer than the return period. Never overdue while no period is set. */
export function isChartOverdue(issuedAt: Date, returnHours: number | null, now: Date): boolean {
  const due = chartDueAt(issuedAt, returnHours);
  return due !== null && now.getTime() > due.getTime();
}

export type ChartTimeErrorCode = 'chart_time.future' | 'chart_time.before_previous';

/** A movement's time: not in the future, not before the chart's previous movement. */
export function checkChartMoveTime(
  at: Date,
  previous: Date | null,
  now: Date,
): Result<Date, ChartTimeErrorCode> {
  if (at.getTime() > now.getTime() + CLOCK_SKEW_MS) return err('chart_time.future');
  if (previous !== null && at.getTime() < previous.getTime()) {
    return err('chart_time.before_previous');
  }
  return ok(at);
}
